import React, {useState, useCallback, useEffect, useRef} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {toast} from 'react-toastify'
import {useFlags} from 'launchdarkly-react-client-sdk'
import {TabToggle} from '../../../shared/Tabs'
import {AccountRow} from '../components/AccountRow'
import {Input} from '../../../shared/Input'
import {Button} from '../../../shared/Button'
import {Spinner} from '../../../../components/loader-spinner'
import {useAuthClient} from '../../../../utils/use-auth-client'
import {fetchAccounts} from '../../../../app-store/accounts/accountsThunk'
import {resetAccounts} from '../../../../app-store/accounts/accountsSlice'
import {accountsApi} from '../../../../app-store/accounts/accountsApi'
import {
  setCountrySelectState,
  setSelectedCountryStates,
  selectCountrySelectState,
} from '../../../../app-store/AccountSetupSlice'
import countryRegionJSON from '../../../../utils/country-region.json'
import {fetchSelectedCountryRegionList} from '../../AccountInfo/services/locationAPI'
import {useAsync} from '../../../../utils/use-async'
import {useAddressValidation} from '../hooks/useAddressValidation'
import Drawer from '../../../shared/Drawer'
import AccountDetailsDrawer from '../components/AccountDetailsDrawer'
import SubscriptionDetailsDrawer from '../components/SubscriptionDrawer'
import Modal from '../../../shared/Modal'
import {AccountEditModal} from '../components/AccounEditModal'
import {formValuesToBillingPayload} from '../AllAccounts/AllAccountsFormMapping'
import {SandboxForm, TIER_OPTIONS} from './SandboxForm'
import {
  ContentArea,
  Toolbar,
  SearchWrapper,
  SearchRow,
  AccountList,
  StatusMessage,
} from '../AccountsTab.styles'
import {StyledSearchIcon} from '../../../shared/icons/icons.styles'
import useDebounce from '../../../../utils/useDebounce'
import {getAccountTabs} from '../../../../utils/account-management-navigation'
import {selectEffectiveRole} from '../../../../app-store/selector'
import {useUserFeatures} from '../../../../context/user-features-context'

const SANDBOX_TIER_CODES = TIER_OPTIONS.map(o => o.value)

const MIN_NON_EMAIL_SEARCH_LENGTH = 5

const isSearchReady = value => {
  const trimmed = (value || '').trim()
  if (!trimmed) return false
  if (trimmed.includes('@')) {
    const at = trimmed.indexOf('@')
    const dot = trimmed.indexOf('.', at)
    return dot > at + 1 && dot < trimmed.length - 1
  }
  return trimmed.length >= MIN_NON_EMAIL_SEARCH_LENGTH
}

export function KeapSandbox() {
  const dispatch = useDispatch()
  const client = useAuthClient()
  const effectiveRoles = useSelector(selectEffectiveRole)
  const userFeatures = useUserFeatures()
  const accountTabs = getAccountTabs(effectiveRoles, userFeatures)
  const {frontendOverrideSst} = useFlags()
  const {selectedCountry} = useSelector(selectCountrySelectState)

  const [searchQuery, setSearchQuery] = useState('')
  const [hasSearched, setHasSearched] = useState(false)
  const debouncedSearch = useDebounce(searchQuery, 400)
  const preloadedSearchRef = useRef(null)

  const [selectedAccount, setSelectedAccount] = useState(null)
  const [selectedLoc, setSelectedLoc] = useState(null)
  const [subCounts, setSubCounts] = useState({})
  const [subCache, setSubCache] = useState({})
  // accountId → Promise<subsData> — shared between background fetch and edit modal
  const subsFetchRef = useRef({})
  // accountId → Promise<accountData> / accountId → accountData — pre-fetched at search time
  const parentFetchRef = useRef({})
  const parentCache = useRef({})

  const [editingTarget, setEditingTarget] = useState(null)
  const [editValues, setEditValues] = useState({})
  const [saving, setSaving] = useState(false)
  const [addressErrors, setAddressErrors] = useState({})
  const [addressSuggestion, setAddressSuggestion] = useState(null)
  const {run} = useAsync({status: 'idle'})
  const {validateAddress, isValidatingAddress} = useAddressValidation(
    client,
    run,
  )

  const [sandboxParent, setSandboxParent] = useState(null)
  const [sandboxSaving, setSandboxSaving] = useState(false)
  const sandboxFormRef = useRef(null)

  // { loc, accountId, loading, initialTier }
  const [editingSandbox, setEditingSandbox] = useState(null)
  const [sandboxEditSaving, setSandboxEditSaving] = useState(false)
  const sandboxEditFormRef = useRef(null)
  // accountId → { loc, initialTier } — persists across modal open/close
  const sandboxEditCache = useRef({})

  const {results, loading, error} = useSelector(
    state => state.accounts.accounts,
  )

  const accounts = results
    .filter(({parent}) => parent != null)
    .map(({parent, children}) => ({
      ...parent,
      locations: (children || [])
        .filter(c => c.isSandbox === true)
        .map(child => {
          const childId = child._fromBillingService
            ? child.id
            : child.code || child.id
          return {
            ...child,
            name: child.company || child.companyName || child.name,
            city: child.city || child.address?.city,
            stateCode:
              child.stateCode || child.address?.region || child.address?.state,
            subs: subCounts[childId] ?? child.subs ?? null,
          }
        }),
    }))

  useEffect(() => {
    const sandboxChildren = results
      .filter(({parent}) => parent != null)
      .flatMap(({children}) =>
        (children || []).filter(c => c.isSandbox === true),
      )
    setSubCounts({})
    setSubCache({})
    subsFetchRef.current = {}
    parentFetchRef.current = {}
    parentCache.current = {}

    // Pre-fetch account-details for each parent so Edit parent modal and Add Sandbox
    // form don't need to fire a new request on click — reuse the in-flight promise.
    results
      .filter(({parent}) => parent != null)
      .forEach(({parent}) => {
        const parentId = parent._fromBillingService
          ? parent.id
          : parent.code || parent.id
        if (!parentId) return
        parentFetchRef.current[parentId] = accountsApi
          .getAccountById(client, parentId)
          .then(fresh => {
            parentCache.current[parentId] = fresh
            return fresh
          })
          .catch(() => null)
      })

    if (sandboxChildren.length === 0) return
    const childIds = [
      ...new Set(
        sandboxChildren
          .map(c => (c._fromBillingService ? c.id : c.code || c.id))
          .filter(Boolean),
      ),
    ]
    if (childIds.length === 0) return
    const counts = {}
    const cache = {}
    childIds.forEach(thryvId => {
      subsFetchRef.current[thryvId] = client(
        `accounts/5/subscriptions?thryvId=${thryvId}`,
      ).catch(() => null)
    })
    Promise.all(
      childIds.map(thryvId =>
        subsFetchRef.current[thryvId].then(data => {
          if (!data) return
          const EXCLUDED = ['app-websitebuilder', 'addon-5-signatures_usage']
          const subs = (data?.childSubscriptions ?? []).filter(
            sub => !EXCLUDED.includes(sub.code),
          )
          const n = subs.reduce(
            (acc, sub) =>
              acc +
              1 +
              (sub.addons?.filter(a => !EXCLUDED.includes(a.code)).length ?? 0),
            0,
          )
          counts[thryvId] = n
          cache[thryvId] = data
        }),
      ),
    ).then(() => {
      setSubCounts(counts)
      setSubCache(cache)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results])

  useEffect(() => {
    return () => {
      dispatch(resetAccounts())
    }
  }, [dispatch])

  const runSearch = useCallback(
    (rawValue, {explicit = false} = {}) => {
      const value = (rawValue ?? '').trim()
      if (!value) return
      setHasSearched(true)
      const isEmail = value.includes('@')
      dispatch(
        fetchAccounts({
          client,
          email: isEmail ? value : undefined,
          thryvId: !isEmail ? value : undefined,
        }),
      )
    },
    [dispatch, client],
  )

  const handleSearchChange = useCallback(
    e => {
      setSearchQuery(e.target.value)
      setHasSearched(false)
      dispatch(resetAccounts())
    },
    [dispatch],
  )

  const handleClearSearch = useCallback(() => {
    setSearchQuery('')
    setHasSearched(false)
    dispatch(resetAccounts())
  }, [dispatch])

  const handleSearch = useCallback(
    input => {
      const raw =
        typeof input === 'string' ? input : input?.target?.value ?? searchQuery
      runSearch(raw, {explicit: true})
    },
    [runSearch, searchQuery],
  )

  useEffect(() => {
    if (!isSearchReady(debouncedSearch)) return
    if (debouncedSearch.trim() === (preloadedSearchRef.current || '').trim()) {
      preloadedSearchRef.current = null
      return
    }
    runSearch(debouncedSearch, {explicit: false})
  }, [debouncedSearch, runSearch])

  const handleKeyDown = useCallback(
    e => {
      if (e.key === 'Enter') handleSearch()
    },
    [handleSearch],
  )

  // Edit parent

  const loadCountryStates = useCallback(
    country => {
      const resolved = country || 'US'
      dispatch(setCountrySelectState(resolved))
      dispatch(
        setSelectedCountryStates(
          fetchSelectedCountryRegionList(resolved, countryRegionJSON),
        ),
      )
    },
    [dispatch],
  )

  const openEditModal = useCallback(
    async (cachedAccount, type) => {
      const accountId = cachedAccount?._fromBillingService
        ? cachedAccount?.id
        : cachedAccount?.code || cachedAccount?.thryvId || cachedAccount?.id
      const cachedCountry =
        cachedAccount?.address?.country ||
        cachedAccount?.country ||
        cachedAccount?.countryCode ||
        'US'
      loadCountryStates(cachedCountry)
      setEditValues({})
      setAddressSuggestion(null)
      const alreadyCached = parentCache.current[accountId]
      setEditingTarget({
        account: alreadyCached
          ? {...cachedAccount, ...alreadyCached}
          : cachedAccount,
        type,
        accountId,
        loading: !alreadyCached && Boolean(accountId),
        isPre5: !!cachedAccount?.isPre5,
      })
      if (alreadyCached) {
        if (alreadyCached?.country || alreadyCached?.address?.country) {
          loadCountryStates(
            alreadyCached.country || alreadyCached.address?.country,
          )
        }
        return
      }
      if (!accountId) return
      try {
        // Reuse the in-flight pre-fetch if available, otherwise fire a new request
        const fresh = await (parentFetchRef.current[accountId] ??
          accountsApi.getAccountById(client, accountId))
        setEditingTarget(prev =>
          prev?.accountId === accountId
            ? {...prev, account: {...cachedAccount, ...fresh}, loading: false}
            : prev,
        )
        if (fresh?.country || fresh?.address?.country) {
          loadCountryStates(fresh.country || fresh.address?.country)
        }
      } catch {
        setEditingTarget(prev =>
          prev?.accountId === accountId ? {...prev, loading: false} : prev,
        )
      }
    },
    [client, loadCountryStates],
  )

  const handleEditParent = useCallback(
    account => openEditModal(account, 'parent'),
    [openEditModal],
  )

  const resolveEditCountry = useCallback(
    () => editingTarget?.account?.address?.country || selectedCountry,
    [editingTarget, selectedCountry],
  )

  const runAddressValidation = useCallback(async () => {
    setAddressErrors({})
    setAddressSuggestion(null)
    const result = await validateAddress({
      ...editValues,
      country: resolveEditCountry(),
    })
    if (!result.success && result.fieldErrors) {
      setAddressErrors(result.fieldErrors)
    }
    if (!result.success && result.suggestion) {
      setAddressSuggestion(result.suggestion)
    }
    if (result.success && result.values) {
      setEditValues(prev => ({...prev, ...result.values}))
    }
    return result
  }, [validateAddress, editValues, resolveEditCountry])

  const saveAccount = useCallback(async () => {
    const {accountId, account} = editingTarget || {}
    if (!accountId) return {ok: true}
    setSaving(true)
    try {
      const payload = formValuesToBillingPayload(editValues, account)
      await accountsApi.updateAccount(client, accountId, payload)
      return {ok: true}
    } catch {
      toast.error('Failed to save account. Please try again.')
      return {ok: false}
    } finally {
      setSaving(false)
    }
  }, [client, editingTarget, editValues])

  const handleSaveInfo = useCallback(async () => {
    const addressResult = await runAddressValidation()
    if (!addressResult.success) return
    const {ok} = await saveAccount()
    if (!ok) return
    toast.success(
      `${
        editValues.businessName || editingTarget?.account?.company || ''
      } updated successfully.`,
    )
    setEditingTarget(null)
    setEditValues({})
    runSearch(searchQuery, {explicit: true})
  }, [
    runAddressValidation,
    saveAccount,
    editValues,
    editingTarget,
    runSearch,
    searchQuery,
  ])

  const handleEditClose = useCallback(() => {
    setEditingTarget(null)
    setEditValues({})
    setAddressErrors({})
    setAddressSuggestion(null)
  }, [])

  // Create sandbox

  const handleAddSandbox = useCallback(
    account => {
      const accountId = account?._fromBillingService
        ? account?.id
        : account?.code || account?.id
      const country =
        account?.address?.country ||
        account?.country ||
        account?.countryCode ||
        'US'
      loadCountryStates(country)

      // Enrich with pre-fetched account-details (has firstName/lastName absent in getAccounts)
      const cached = parentCache.current[accountId]
      setSandboxParent(cached ? {...account, ...cached} : account)

      // If the fetch is still in flight, update sandboxParent when it resolves
      if (!cached && parentFetchRef.current[accountId]) {
        parentFetchRef.current[accountId].then(fresh => {
          if (!fresh) return
          setSandboxParent(prev => {
            if (!prev) return prev
            const prevId = prev._fromBillingService
              ? prev.id
              : prev.code || prev.id
            return prevId === accountId ? {...prev, ...fresh} : prev
          })
        })
      }
    },
    [loadCountryStates],
  )

  const handleEditSandbox = useCallback(
    async loc => {
      const accountId = loc?._fromBillingService
        ? loc?.id
        : loc?.code || loc?.id
      const country =
        loc?.address?.country || loc?.country || loc?.countryCode || 'US'
      loadCountryStates(country)

      const cached = sandboxEditCache.current[accountId]
      if (cached) {
        setEditingSandbox({
          loc: cached.loc,
          accountId,
          loading: false,
          initialTier: cached.initialTier,
        })
        return
      }

      setEditingSandbox({loc, accountId, loading: true, initialTier: null})
      try {
        const subsPromise =
          subsFetchRef.current[accountId] ??
          client(`accounts/5/subscriptions?thryvId=${accountId}`).catch(
            () => null,
          )
        const [freshAccount, subsData] = await Promise.all([
          accountsApi.getAccountById(client, accountId).catch(() => null),
          subsPromise,
        ])
        const initialTier =
          (subsData?.childSubscriptions ?? []).find(s =>
            SANDBOX_TIER_CODES.includes(s.code),
          )?.code ?? null
        const freshLoc = freshAccount ? {...loc, ...freshAccount} : loc
        sandboxEditCache.current[accountId] = {loc: freshLoc, initialTier}
        setEditingSandbox(prev =>
          prev?.accountId === accountId
            ? {...prev, loc: freshLoc, loading: false, initialTier}
            : prev,
        )
      } catch {
        setEditingSandbox(prev =>
          prev?.accountId === accountId ? {...prev, loading: false} : prev,
        )
      }
    },
    [client, loadCountryStates],
  )

  const handleUpdateSandbox = useCallback(
    async payload => {
      const {accountId, loc, initialTier} = editingSandbox || {}
      setSandboxEditSaving(true)
      try {
        const tierChanged = payload.tierPlanCode !== initialTier
        const billingPayload = {
          firstName: payload.firstName,
          lastName: payload.lastName,
          company: payload.company,
          email: payload.email,
          mobile: payload.phone,
          billTo: loc?.billTo ?? 'parent',
          parentId: loc?.parentAccountId ?? null,
          address: {
            street1: payload.address?.street,
            city: payload.address?.city,
            state: payload.address?.state,
            postalCode: payload.address?.zipCode,
            country: payload.address?.country || loc?.address?.country || 'US',
            phone: payload.phone,
          },
          name: payload.company,
        }
        await Promise.all([
          accountsApi.updateAccount(client, accountId, billingPayload),
          tierChanged
            ? client('sandbox/switch-tier', {
                method: 'POST',
                data: {
                  accountCode: accountId,
                  newTierPlanCode: payload.tierPlanCode,
                },
              })
            : Promise.resolve(),
        ])
        toast.success('Sandbox updated successfully.')
        delete sandboxEditCache.current[accountId]
        setEditingSandbox(null)
        runSearch(searchQuery, {explicit: true})
      } catch (err) {
        toast.error(err?.message || 'Failed to update sandbox.')
      } finally {
        setSandboxEditSaving(false)
      }
    },
    [client, editingSandbox, runSearch, searchQuery],
  )

  const handleCreateSandbox = useCallback(
    async payload => {
      const parentCcId = sandboxParent?._fromBillingService
        ? sandboxParent?.id
        : sandboxParent?.code || sandboxParent?.thryvId || sandboxParent?.id
      // CC-prefixed IDs are Recurly account codes, not intg-signup org IDs — discard them
      // so the backend falls through to the Recurly fallback for legacy CC parents (FULSAS-14126).
      const parentOrgId =
        sandboxParent?.parentOrgId && !/^CC_/i.test(sandboxParent.parentOrgId)
          ? sandboxParent.parentOrgId
          : null
      setSandboxSaving(true)
      try {
        await client('sandbox/create', {
          method: 'POST',
          data: {parentCcId, parentOrgId, ...payload},
        })
        toast.success('Sandbox created successfully.')
        setSandboxParent(null)
        runSearch(searchQuery, {explicit: true})
      } catch (err) {
        toast.error(err?.message || 'Failed to create sandbox.')
      } finally {
        setSandboxSaving(false)
      }
    },
    [client, sandboxParent, runSearch, searchQuery],
  )

  return (
    <>
      <ContentArea>
        <Toolbar>
          <TabToggle tabs={accountTabs} />
          <SearchRow>
            <SearchWrapper>
              <Input
                value={searchQuery}
                onChange={handleSearchChange}
                onKeyDown={handleKeyDown}
                leftIcon={<StyledSearchIcon />}
                rightIcon={searchQuery ? '×' : null}
                onRightIconClick={searchQuery ? handleClearSearch : undefined}
                placeholder="Search by email, Thryv ID, or EAID"
                variant="form"
                size="md"
              />
            </SearchWrapper>
          </SearchRow>
        </Toolbar>

        {loading && <Spinner />}
        {!loading && error && <StatusMessage isError>{error}</StatusMessage>}
        {!loading && !error && !hasSearched && (
          <StatusMessage>
            Search for an account to view its sandboxes.
          </StatusMessage>
        )}
        {!loading && !error && hasSearched && accounts.length === 0 && (
          <StatusMessage>No sandbox accounts found.</StatusMessage>
        )}
        {!loading && !error && accounts.length > 0 && (
          <AccountList aria-label="Sandbox accounts">
            {accounts.map(account => (
              <AccountRow
                key={account.id}
                account={account}
                defaultOpen
                isSandboxContext
                onViewDetails={() => setSelectedAccount(account)}
                onEdit={() => handleEditParent(account)}
                onAddSandbox={() => handleAddSandbox(account)}
                onEditSandbox={loc => handleEditSandbox(loc)}
                onViewSubscription={loc => {
                  const locId = loc?._fromBillingService
                    ? loc.id
                    : loc?.code || loc?.id
                  setSelectedLoc({
                    ...loc,
                    prefetchedSubscriptions: subCache[locId] ?? null,
                  })
                }}
                showStatus
              />
            ))}
          </AccountList>
        )}
      </ContentArea>

      <Drawer
        isOpen={!!selectedAccount}
        onClose={() => setSelectedAccount(null)}
        direction="right"
        size="600px"
      >
        {selectedAccount && (
          <AccountDetailsDrawer
            account={selectedAccount}
            onClose={() => setSelectedAccount(null)}
            isSandboxContext
          />
        )}
      </Drawer>

      <Drawer
        isOpen={!!selectedLoc}
        onClose={() => setSelectedLoc(null)}
        direction="right"
        size="600px"
      >
        {selectedLoc && (
          <SubscriptionDetailsDrawer
            loc={selectedLoc}
            prefetchedData={selectedLoc?.prefetchedSubscriptions ?? null}
            onClose={() => setSelectedLoc(null)}
            source="keap-sandbox"
          />
        )}
      </Drawer>

      <Modal
        isOpen={!!editingSandbox}
        onClose={sandboxEditSaving ? () => {} : () => setEditingSandbox(null)}
        showCloseButton={!sandboxEditSaving}
        closeOnBackdrop={false}
        closeOnEsc={!sandboxEditSaving}
        title="Edit Sandbox"
        subtitle={editingSandbox?.loc?.company || editingSandbox?.loc?.name}
        size="lg"
        footerAlign="right"
        footer={
          <Button
            variant="solid-gradiant"
            onClick={() => sandboxEditFormRef.current?.submit()}
            disabled={sandboxEditSaving || editingSandbox?.loading}
          >
            {sandboxEditSaving ? 'Saving…' : 'Save Changes'}
          </Button>
        }
      >
        {editingSandbox && (
          <SandboxForm
            ref={sandboxEditFormRef}
            mode="edit"
            sandbox={editingSandbox.loc}
            initialTier={editingSandbox.initialTier}
            loading={editingSandbox.loading}
            onSubmit={handleUpdateSandbox}
          />
        )}
      </Modal>

      <Modal
        isOpen={!!sandboxParent}
        onClose={sandboxSaving ? () => {} : () => setSandboxParent(null)}
        showCloseButton={!sandboxSaving}
        closeOnBackdrop={false}
        closeOnEsc={!sandboxSaving}
        title="Add Sandbox"
        subtitle={
          sandboxParent?.company ||
          sandboxParent?.companyName ||
          [sandboxParent?.firstName, sandboxParent?.lastName]
            .filter(Boolean)
            .join(' ')
        }
        size="lg"
        footerAlign="right"
        footer={
          <Button
            variant="solid-gradiant"
            onClick={() => sandboxFormRef.current?.submit()}
            disabled={sandboxSaving}
          >
            {sandboxSaving ? 'Creating…' : 'Create Sandbox'}
          </Button>
        }
      >
        {sandboxParent && (
          <SandboxForm
            ref={sandboxFormRef}
            parent={sandboxParent}
            onSubmit={handleCreateSandbox}
          />
        )}
      </Modal>

      <Modal
        isOpen={!!editingTarget}
        onClose={saving || isValidatingAddress ? () => {} : handleEditClose}
        showCloseButton={!saving && !isValidatingAddress}
        closeOnBackdrop={false}
        closeOnEsc={!saving && !isValidatingAddress}
        title="Edit Account details"
        subtitle={
          editingTarget?.account?.company || editingTarget?.account?.companyName
        }
        size="xl"
        footerAlign="right"
        footer={
          <Button
            variant="secondary-blue"
            onClick={handleSaveInfo}
            disabled={saving || isValidatingAddress || editingTarget?.loading}
          >
            {isValidatingAddress
              ? 'Validating…'
              : saving
              ? 'Saving…'
              : 'Save Information'}
          </Button>
        }
      >
        {editingTarget && (
          <AccountEditModal
            roles={effectiveRoles}
            account={editingTarget.account}
            type={editingTarget.type}
            onValuesChange={setEditValues}
            loading={editingTarget.loading}
            saving={saving}
            isValidatingAddress={isValidatingAddress}
            addressErrors={addressErrors}
            addressSuggestion={addressSuggestion}
            onApplySuggestion={() => setAddressSuggestion(null)}
            frontendOverrideSst={frontendOverrideSst}
            isOrderFlow={false}
            isPre5={!!editingTarget?.isPre5}
          />
        )}
      </Modal>
    </>
  )
}
